/* eslint-disable @typescript-eslint/no-explicit-any */
import { compose, pick } from 'ramda';
import React, { useEffect, useState, useRef } from 'react';
import Slider from 'js/components/common/Slider';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { withComponent } from 'js/components/hocs/withComponent';
import {
    useComponentConfig,
    useComponentTranslations,
    useComponentMethods,
    useComponentProps
} from 'js/hooks/component';
import { Loader } from 'js/components/common/Loader';
import * as hooks from 'js/components/main/Banner/hooks';
import * as actions from 'js/components/main/Banner/store/actions';
import { Slide } from 'js/components/main/Banner/components/Slide';
import {
    withDynamicReducer,
    useDynamicReducerDispatch,
    useDynamicReducerSelector
} from 'js/components/main/Banner/store';
import style from 'js/components/main/Banner/Banner.scss';

declare global {
    interface Window {
        DYO: any;
        DY: any;
    }
}
export const BannerBare: React.FC = () => {
    const isMounted = useIsMounted();
    const getSlides = hooks.useGetSlides();
    const bannerRef = useRef<any>(null);
    const translate = useComponentTranslations();
    const { bannerFeedName } = useComponentConfig();
    const { portletInfo } = useComponentProps();
    const { customCSSClassName } = portletInfo?.look || {};
    const { showComponentError } = useComponentMethods();
    const dynamicReducerDispatch = useDynamicReducerDispatch();
    const { slides, sliderOptions } = useDynamicReducerSelector(pick(['slides', 'sliderOptions']));
    const isDY = window.DYO && window.DYO.hasOwnProperty('getUserObjectsAndVariations') && customCSSClassName !== 'swiss-banner';
    const [loading, setLoading] = useState(true);
    const bannerHeight = sliderOptions?.height;
    const waitForDYBanner = () => new Promise((resolve) => {
        const ms = 500; let triesLeft = 0;
        const dyBannerTimeout = window.Playtech.Variables.dyBannerTimeout || 3000;
        const interval = setInterval(() => {
            const isLatest = triesLeft >= dyBannerTimeout;

            if (window.DY.BannerIn || isLatest) {
                resolve(Boolean(window.DY.BannerIn));
                clearInterval(interval);
            }

            triesLeft += ms;
            console.log('wait for DY object. Time = ', triesLeft);
        }, ms);
    });

    const callDyObject = () => {
        window.DYO.smartObject('Test Slider', {
            target: 'target_selector',
            inline: true
        }) ;
        console.log('DY smartObject is called');
    };

    useEffect(() => {
        if (bannerRef?.current) {
            const bannerPortlet = bannerRef.current.parentElement.parentElement;
            if (bannerPortlet.classList.contains('portlet_name_banner') && !loading) {
                bannerRef.current.parentElement.parentElement.setAttribute('width', '100%');
                bannerRef.current.parentElement.parentElement.setAttribute('height', bannerHeight);
            }
        }
    }, [loading, bannerRef]);

    useEffect(() => {
        if (!bannerFeedName) {
            showComponentError(translate('empty'));
        }
    }, [bannerFeedName, translate, showComponentError]);

    useEffect(() => {
        if (!bannerFeedName) {
            return;
        }

        getSlides()
            .then((response) => {
                isMounted() && dynamicReducerDispatch(actions.setSlidesAndOptions(response));
            })
            .catch((response) => {
                isMounted() && dynamicReducerDispatch(actions.setSlidesAndOptions(response));
            });
    }, [getSlides, dynamicReducerDispatch, isMounted, bannerFeedName]);

    useEffect(() => {
        isDY ? waitForDYBanner()
                .then(() => callDyObject())
                .then(() => setLoading(false))
                .catch(() => console.log('error DY inserting')) :
            setLoading(false);
    }, [isDY]);

    if (!slides || !slides.length) {
        return null;
    }

    return (

    <div ref={bannerRef}>
        <div className={style.banner}>
            {isDY ?
                (<div id='target_selector' className={loading ? style.hiddenBanner : style.showingBanner}/>) :
                (<Slider options={sliderOptions}
                                 classNames={{
                                     slider: style.slider,
                                     bullets: style.bullets,
                                     leftArrow: style.leftArrow,
                                     rightArrow: style.rightArrow,
                                     sliderContainer: style.sliderContainer
                                 }}
                                 autoRotate={true}
                >
                    {slides.map((slide) => <Slide key={slide.id} slide={slide} />)}
                </Slider>)}
            {loading ? <Loader/> : <div/>}
        </div>
    </div>
    );
};

export const Banner = compose(
    withComponent({ tkPrefix: 'playtech.mobile.system.banner.' }),
    withDynamicReducer()
)(BannerBare);

export default Banner;
